import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Modal, Row, Col, Spinner } from "react-bootstrap";
import { InfoCircle, User } from 'iconsax-react';
import { TabellaListaServer } from '../../../../components/Tabella/TabellaListaServer';
import { ChipFilter } from '../../../../components/Tabella/ChipFilter';
import { useRememberedState } from '../../../../components/Tabella/listState';
import { RangeFilterButton } from '../../../../components/Tabella/RangeFilterButton';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { sports } from '../../../../components/Util/enum';
import { nomiSport, sportDellaTessera } from '../../../../components/Util/sportProdotto';
import { useFederations } from '../../../../../hooks/useFederations';
import { useProfile } from '../../../../../hooks/useProfile';

const PAGE_SIZE = 10;

// La ricerca la tiene la tabella (casella accanto ai filtri): qui solo il resto.
const FILTRI_INIZIALI = {
	dateFrom: '',
	dateTo: '',
	attiva: '',
	sport: '',
	organismo: '',
	archivio: ''
};

const TESSERA_OPTIONS = [
	{ value: 'si', label: 'Attiva' },
	{ value: 'no', label: 'Non attiva' }
];

// Le date arrivano come 'YYYY-MM-DD': confrontarle come stringhe evita del tutto
// il fuso orario, che con new Date('YYYY-MM-DD') sposta il giorno a ovest di Greenwich.
const soloData = (valore) => (valore ? String(valore).substring(0, 10) : '');

// Cerchio da 28px come nel mockup, con la foto del profilo quando c'è — stesso
// comportamento della lista player. Il segnaposto arancione è il ripiego.
const AvatarTesserato = ({ imageUrl, name }) => (
	<span
		className="d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 overflow-hidden"
		style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(253, 126, 20, 0.12)', color: '#fd7e14' }}
	>
		{imageUrl
			? <img src={imageUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
			: <User size="15" variant="Linear" />}
	</span>
);

const formatDate = (valore) => {
	const iso = soloData(valore);
	if (!iso) return '—';
	const [anno, mese, giorno] = iso.split('-');
	if (!anno || !mese || !giorno) return iso;
	return `${giorno}/${mese}/${anno}`;
};

const oggiISO = () => {
	const d = new Date();
	const mese = String(d.getMonth() + 1).padStart(2, '0');
	const giorno = String(d.getDate()).padStart(2, '0');
	return `${d.getFullYear()}-${mese}-${giorno}`;
};

// Una tessera è attiva se oggi cade dentro la sua validità. Il filtro lo applica
// il server; qui serve solo a colorare la pill di stato.
const tesseraAttiva = (riga) => {
	const oggi = oggiISO();
	if (riga.dateEnd && riga.dateEnd < oggi) return false;
	if (riga.dateStart && riga.dateStart > oggi) return false;
	return true;
};

export const TesseratiTable = () => {

	const { federations } = useFederations();
	const { profile: profileData } = useProfile({ autoFetch: true });

	// Filtri, ordinamento e paginazione sono lato server: qui sta solo la pagina
	// corrente. La ricerca arriva dalla tabella gia' ritardata (600ms, da 3
	// caratteri in su); i filtri le passano come `externalFilters`, e quando
	// cambiano la tabella torna da sola alla prima pagina.
	const [data_json, setDataJson] = useState(null);
	const [total, setTotal] = useState(0);
	const [pageCount, setPageCount] = useState(1);
	// Si ricordano uscendo e rientrando nella pagina, comprese le tendine Organismo e Registro
	const [filtri, setFiltri] = useRememberedState('filtri', FILTRI_INIZIALI);
	const [ricercaApplicata, setRicercaApplicata] = useState('');


	const [modalShowed, setModalShowed] = useState(false);
	const [modalTitle, setModalTitle] = useState('');
	const [modalText, setModalText] = useState('');

	const [loadingShowed, setLoadingShowed] = useState(false);


	const navigate = useNavigate();

	const getToken = () => {
		const tokenDetailsString = sessionStorage.getItem('userDetails');
		if (!tokenDetailsString) return null;
		return JSON.parse(tokenDetailsString).idToken;
	};

	const mostraErrore = (testo) => {
		setModalTitle("Errore durante l'operazione");
		setModalText(testo);
		setModalShowed(true);
	};

	// Parametri comuni a lista ed export.
	const parametriFiltri = (search, f) => ({
		search,
		dateFrom: f.dateFrom,
		dateTo: f.dateTo,
		// Il server trova la tessera se lo sport e' uno qualsiasi dei suoi sport
		sportType: f.sport,
		federationCode: f.organismo,
		...(f.attiva && { active: f.attiva === 'si' }),
		...(f.archivio === 'si' && { archived: true })
	});

	const caricaDati = useCallback(async (pageIndex, pageSize, search, filtriApplicati) => {
		const token = getToken();
		if (!token) return;

		const apiClient = new MyApiClient();
		setLoadingShowed(true);
		const tesseratiData = await apiClient.getTesserati(token, {
			offset: pageIndex * pageSize,
			limit: pageSize,
			...parametriFiltri(search, filtriApplicati)
		});
		setLoadingShowed(false);

		if (!tesseratiData || tesseratiData.error) {
			setDataJson([]);
			setTotal(0);
			setPageCount(1);
			mostraErrore("Non è stato possibile caricare il registro tesserati: " + ((tesseratiData && tesseratiData.error) || 'errore generico'));
			return;
		}

		const righe = (tesseratiData.items || []).map(element => ({
			id: element.customerUserId,
			licenseId: element.licenseId,
			licenseNumber: element.licenseNumber,
			name: element.name,
			userType: element.userType,
			imageUrl: element.picture && element.picture.uri ? element.picture.uri : null,
			// Una tessera copre tutti gli sport del prodotto che l'ha emessa: si mostrano tutti
			sportType: element.sportType,
			sportTypes: sportDellaTessera(element),
			sportLabel: nomiSport(element) || '—',
			federationCode: element.federationCode || '',
			dateStart: soloData(element.dateStart),
			dateEnd: soloData(element.dateEnd),
			original: element
		}));

		setDataJson(righe);
		setTotal(tesseratiData.total || 0);
		setPageCount(Math.max(1, Math.ceil((tesseratiData.total || 0) / pageSize)));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// La tabella guida paginazione, ricerca e filtri; qui si ricorda l'ultima
	// richiesta per ricaricare dopo una modifica.
	const ultimaRichiesta = useRef({ pageIndex: 0, pageSize: PAGE_SIZE, search: '', filtri: FILTRI_INIZIALI });
	const fetchData = useCallback(({ pageIndex, pageSize, globalFilter, externalFilters }) => {
		const search = (globalFilter || '').trim();
		ultimaRichiesta.current = { pageIndex, pageSize, search, filtri: externalFilters || FILTRI_INIZIALI };
		setRicercaApplicata(search);
		caricaDati(pageIndex, pageSize, search, ultimaRichiesta.current.filtri);
	}, [caricaDati]);


	// Gli organismi filtrabili sono quelli configurati dalla struttura per i propri
	// sport: la pagina corrente non basta più a dedurli, ora che è una sola pagina.
	const opzioniOrganismo = useMemo(() => {
		const codici = new Set();
		(profileData?.club?.sports || []).forEach(sport => {
			(sport.sportPromotionBody || []).forEach(codice => codici.add(codice));
		});
		return [...codici].sort().map(codice => {
			const federazione = federations.find(f => f.code === codice);
			return { value: codice, label: federazione ? `${codice} - ${federazione.description}` : codice };
		});
	}, [profileData, federations]);

	// Il filtro Sport propone gli sport della struttura (sempre una tendina, regola SUI)
	const opzioniSport = useMemo(() => {
		const codici = new Set((profileData?.club?.sports || []).map(sport => sport.sportType));
		return sports
			.filter(sport => codici.has(sport.value))
			.map(sport => ({ value: sport.value, label: sport.label }))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [profileData]);

	const aggiornaFiltro = (campo, valore) => setFiltri(prec => ({ ...prec, [campo]: valore }));

	// L'export rispetta i filtri attivi, non solo la pagina a schermo.
	const esporta = async () => {
		const token = getToken();
		if (!token) return;

		const apiClient = new MyApiClient();
		setLoadingShowed(true);
		const blob = await apiClient.exportTesserati(token, parametriFiltri(ricercaApplicata, filtri));
		setLoadingShowed(false);

		if (!blob || blob.error) {
			mostraErrore("Non è stato possibile generare l'export.");
			return;
		}

		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', 'elenco-tesserati.xlsx');
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);
	};

	// Una tessera archiviata è un record chiuso: non si modifica e non si rielimina.
	const inArchivio = filtri.archivio === 'si';

	const data_column = [
		{
			Header: 'N. tessera',
			accessor: 'licenseNumber',
			Cell: ({ row }) => (
				<span className="text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
					{row.original.licenseNumber || '—'}
				</span>
			),
			disableFilters: true
		},
		{
			Header: 'Nome e Cognome',
			accessor: 'name',
			Cell: ({ row }) => (
				<div className="d-flex align-items-center">
					<AvatarTesserato imageUrl={row.original.imageUrl} name={row.original.name} />
					<span>{row.original.name}</span>
				</div>
			),
			disableFilters: true
		},
		{
			Header: 'Organismo',
			accessor: 'federationCode',
			Cell: ({ row }) => row.original.federationCode || '—',
			disableFilters: true
		},
		{
			Header: 'Sport',
			accessor: 'sportLabel',
			disableFilters: true
		},
		{
			Header: 'Inizio validità',
			accessor: 'dateStart',
			Cell: ({ row }) => formatDate(row.original.dateStart),
			disableFilters: true
		},
		{
			Header: 'Fine validità',
			accessor: 'dateEnd',
			Cell: ({ row }) => formatDate(row.original.dateEnd),
			disableFilters: true
		},
		{
			Header: 'Stato',
			accessor: 'stato',
			Cell: ({ row }) => {
				const attiva = tesseraAttiva(row.original);
				return (
					<span className={`sid-pill ${attiva ? 'sid-pill--green' : 'sid-pill--amber'}`}>
						{attiva ? 'Attivo' : 'Non attivo'}
					</span>
				);
			},
			disableFilters: true
		},
	];

	function openDetail(row) {
		// Un tesserato puo` essere un junior, e la scheda del junior e` un'altra
		// pagina: quella del player legge `idsPlay`, che un junior non ha, e si
		// rompe prima ancora di disegnare qualcosa.
		const percorso = row.userType === 'JUNIOR'
			? '/struttura/junior/junior_profile/'
			: '/struttura/clienti/player_profile/';
		navigate(percorso + row.id);
	}

	// La riga apre la scheda della tessera: «Modifica tessera» ed «Elimina tessera» stanno lì (03/10/2026)
	const apriTessera = (row) => {
		if (!row.licenseId) { openDetail(row); return; }
		const { original, ...tessera } = row;
		navigate('/struttura/tesserati/scheda/' + row.licenseId, { state: { tessera, inArchivio } });
	};

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
					<Spinner animation="border" role="status">
						<span className="sr-only">Loading...</span>
					</Spinner>
					<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<Modal className="fade" show={modalShowed} centered>
				<Modal.Header>
					<Modal.Title>{modalTitle}</Modal.Title>
					<Button onClick={() => setModalShowed(false)} variant="" className="btn-close"></Button>
				</Modal.Header>
				<Modal.Body>
					<Row><Col className="col-xl-64">{modalText}</Col></Row>
				</Modal.Body>
				<Modal.Footer className="justify-content-center">
					<Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>Chiudi</Button>
				</Modal.Footer>
			</Modal>

			<TabellaListaServer
				openColumn={'name'}
				openRow={(row) => apriTessera(row)}
				pageList={true}
				cardTitle={'Tesserati'}
				sidLayout={true}
				disableSortBy={true}
				headerActions={
					<div className="d-flex gap-2 flex-wrap">
						<Button className="btn btn-add sid-btn-secondary" onClick={esporta}>Esporta</Button>
						<Button
							className="btn btn-primary btn-add"
							style={{ textTransform: 'none' }}
							onClick={() => navigate('/struttura/tesserati/nuovo')}
						>
							+ Nuovo tesseramento
						</Button>
					</div>
				}
				filterBar={
					<>
						<div className="sid-info-banner">
							<InfoCircle size="18" className="sid-info-icon" />
							<div>
								Il rinnovo non è automatico: il player deve acquistare una nuova tessera.
								A ogni nuova tessera viene assegnato un nuovo numero univoco progressivo.
							</div>
						</div>
					</>
				}
				filterControl={
					<div className="sid-chip-filters">
						<RangeFilterButton
							label="Periodo"
							ranges={[{ key: 'periodo', label: 'Data di tesseramento', type: 'date' }]}
							value={{ periodo: { da: filtri.dateFrom, a: filtri.dateTo } }}
							onApply={(nuovi) => setFiltri(prec => ({ ...prec, dateFrom: nuovi.periodo?.da || '', dateTo: nuovi.periodo?.a || '' }))}
						/>
						<ChipFilter label="Tessera" options={TESSERA_OPTIONS} value={filtri.attiva} onChange={(valore) => aggiornaFiltro('attiva', valore)} />
						<ChipFilter label="Sport" options={opzioniSport} value={filtri.sport || ''} onChange={(valore) => aggiornaFiltro('sport', valore)} asDropdown />
						<div className="sid-filter-bar mb-0">
							<Form.Select value={filtri.organismo} onChange={(e) => aggiornaFiltro('organismo', e.target.value)}>
								<option value="">Tutti gli organismi</option>
								{opzioniOrganismo.map(opzione => (
									<option key={opzione.value} value={opzione.value}>{opzione.label}</option>
								))}
							</Form.Select>
							<Form.Select value={filtri.archivio} onChange={(e) => aggiornaFiltro('archivio', e.target.value)}>
								<option value="">Registro</option>
								<option value="si">Archivio</option>
							</Form.Select>
						</div>
					</div>
				}
				externalFilters={filtri}
				totalItems={total}
				hasSearch={true}
				searchPlaceholder={'Cerca per nome o cognome'}
				hasFilter={false}
				hasAddMore={false}
				data_column={data_column}
				data_json={data_json}
				manualPagination={true}
				pageCount={pageCount}
				fetchData={fetchData}
				sort_by={'licenseNumber'}
				sort_type={'asc'}
				manualGlobalFilter={true}
				manualFilters={false}
			/>
		</>
	)
}
export default TesseratiTable;
